import { useRef, useState, type ChangeEvent } from 'react';
import { useOp } from '../../api.ts';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { ErrorBox } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';

/**
 * A store brand's logo (docs/media.md): upload, fetch from the store's website or an image address, and every
 * earlier version to switch back to. One logo for every site and country of the brand.
 */

interface LogoVersion {
  ref: string;
  url: string;
  small: string;
  width: number;
  height: number;
  format: string;
  sourceType: string;
  sourceUrl: string | null;
  createdAt: string;
  current: boolean;
}

/** Studio reads stored media through the api on its own origin (/admin/media/…). */
export const studioMediaUrl = (path: string) => path.replace(/^\/media\//, '/admin/media/');

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function LogoPanel({ masterRef, canEdit, hasHomepage }: { masterRef: string; canEdit: boolean; hasHomepage: boolean }) {
  const toast = useToast();
  const changes = useChanges();
  const logos = useOp<{ current: string | null; versions: LogoVersion[] }>('admin.masterMerchants.logos', { ref: masterRef }, changes);
  const [busy, setBusy] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const current = logos.data?.versions.find((v) => v.current) ?? null;
  const external = logos.data?.current && !logos.data.current.startsWith('/media/') ? logos.data.current : null;

  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast('error', 'The file is larger than 5 MB');
      return;
    }
    setBusy(true);
    await runWrite<{ reused: boolean }>(toast, 'admin.masterMerchants.uploadLogo', { ref: masterRef, data: await fileToBase64(file), filename: file.name }, (r) => (r.reused ? 'That logo was stored already: it is in use again' : 'Logo uploaded'));
    setBusy(false);
  };

  const fetchLogo = async (url?: string) => {
    setBusy(true);
    await runWrite<{ url: string | null; from: string | null; errors: string[] }>(toast, 'admin.masterMerchants.fetchLogo', { ref: masterRef, ...(url ? { url } : {}) }, (r) =>
      r.url ? `Logo taken from ${r.from}` : `No usable logo found${r.errors[0] ? `: ${r.errors[0]}` : ''}`,
    );
    setBusy(false);
    setImageUrl('');
  };

  return (
    <section aria-labelledby="logo-title" className="logo-panel">
      <h3 id="logo-title" className="section-title">
        Logo
      </h3>
      {logos.error ? <ErrorBox error={logos.error} onRetry={logos.reload} /> : null}
      <div className="logo-current">
        {current ? <img src={studioMediaUrl(current.url)} width={96} height={96} alt="Current logo" className="logo-tile" /> : <span className="logo-tile logo-empty">No logo</span>}
        <div className="small">
          {current ? (
            <>
              <div>
                From {current.sourceType === 'upload' ? `upload${current.sourceUrl ? ` (${current.sourceUrl})` : ''}` : current.sourceUrl} · original {current.width}×{current.height} {current.format}
              </div>
              <div className="muted">Every site shows it on a white tile in 64, 128 and 256 px.</div>
            </>
          ) : (
            <div className="muted">The sites show the initials on the brand colour until a logo is set.</div>
          )}
          {external ? <div className="dev-note">An old external logo address is set ({external}); sites do not show external images. Upload or fetch the logo to use it.</div> : null}
        </div>
      </div>
      {canEdit ? (
        <div className="logo-actions">
          <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/avif,image/svg+xml" hidden onChange={(e) => void upload(e)} />
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => input.current?.click()}>
            Upload logo
          </button>
          <button type="button" className="btn" disabled={busy || !hasHomepage} title={hasHomepage ? 'Looks for the logo on the store’s homepage' : 'Set the homepage first'} onClick={() => void fetchLogo()}>
            Fetch from website
          </button>
          <span className="inline-form">
            <input type="url" placeholder="or an image address https://…" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} aria-label="Image address" />
            <button type="button" className="btn" disabled={busy || !imageUrl.startsWith('http')} onClick={() => void fetchLogo(imageUrl)}>
              Use image
            </button>
          </span>
          {current ? (
            <button type="button" className="btn" disabled={busy} onClick={() => void runWrite(toast, 'admin.masterMerchants.useLogo', { ref: masterRef, assetRef: null }, 'Logo removed: initials shown')}>
              Show initials
            </button>
          ) : null}
        </div>
      ) : null}
      {(logos.data?.versions.length ?? 0) > 1 || (logos.data?.versions.length === 1 && !current) ? (
        <details className="logo-versions">
          <summary>Earlier versions ({logos.data!.versions.filter((v) => !v.current).length})</summary>
          <ul>
            {logos.data!.versions
              .filter((v) => !v.current)
              .map((v) => (
                <li key={v.ref}>
                  <img src={studioMediaUrl(v.small)} width={40} height={40} alt="" className="logo-tile" />
                  <span className="small">
                    {new Date(v.createdAt).toLocaleDateString('en-GB')} · {v.sourceType === 'upload' ? 'upload' : v.sourceUrl}
                  </span>
                  {canEdit ? (
                    <button type="button" className="btn btn-small" disabled={busy} onClick={() => void runWrite(toast, 'admin.masterMerchants.useLogo', { ref: masterRef, assetRef: v.ref }, 'Logo version in use again')}>
                      Use this
                    </button>
                  ) : null}
                </li>
              ))}
          </ul>
          <p className="muted small">Kept for ever, so pages cached with an older logo never show a broken image.</p>
        </details>
      ) : null}
    </section>
  );
}
